import {useFilterContext} from '../contexts/FilterContext'
import {useCategory} from '../contexts/CategoryContext'
import {useCallback, useMemo, useState} from 'react'
import {Filter, type FilterQuery, type FilterProvider} from '@github-ui/filter'
import {useResponsiveValue} from '@primer/react'
import styles from './marketplace-search-field.module.css'
import {filterStringFromQuery, queryFromFilterString} from '../utilities/filters'
import {getListingTypeFilterProvider} from '../utilities/filter-providers'
import {
  getModelsCategoryFilterProvider,
  getModelsPublisherFilterProvider,
  ModelsInputModalityFilterProvider,
  ModelsLanguageFilterProvider,
  ModelsLicenseFilterProvider,
  ModelsOutputModalityFilterProvider,
  ModelsTaskFilterProvider,
} from '../utilities/model-filter-providers'
import {useSearchType} from '../contexts/SearchTypeContext'
import {useCopilotApp} from '../contexts/CopilotAppContext'
import {useQuery} from '../contexts/QueryContext'
import useAppsFilterProviders from '../hooks/use-apps-filter-providers'
import useActionsFilterProviders from '../hooks/use-actions-filter-providers'
import {useModelsSearchFilters} from '../hooks/use-models-search-filters'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const modelsProviders: FilterProvider[] = [
  ModelsInputModalityFilterProvider,
  ModelsLanguageFilterProvider,
  ModelsLicenseFilterProvider,
  ModelsOutputModalityFilterProvider,
  ModelsTaskFilterProvider,
]

export default function MarketplaceSearchField() {
  const {onQueryChange: onQueryChangeV2} = useFilterContext()
  const {query} = useQuery()
  const {copilotApp, setCopilotApp} = useCopilotApp()
  const {type, setType} = useSearchType()
  const {category, setCategory} = useCategory()
  const hideCopilotExtensions = isFeatureEnabled('copilot_extensions_removal_on_marketplace')
  const initialFilter = filterStringFromQuery(type, copilotApp, query, category, hideCopilotExtensions)
  const [privateQuery, setPrivateQuery] = useState<string>(initialFilter)
  const {
    data: {categories: modelsCategories, publishers: modelsPublishers},
  } = useModelsSearchFilters()
  const appsProviders = useAppsFilterProviders()
  const actionsProviders = useActionsFilterProviders()
  const listingTypeProvider: FilterProvider = getListingTypeFilterProvider(hideCopilotExtensions)

  const providers = useMemo(() => {
    const generalProviders = [listingTypeProvider]
    let result: FilterProvider[] = [...generalProviders]

    switch (type) {
      case 'actions':
        result = result.concat(actionsProviders)
        break
      case 'apps':
        result = result.concat(appsProviders)
        break
      case 'models':
        result = result.concat(modelsProviders)
        result.push(getModelsPublisherFilterProvider(modelsPublishers))
        result.push(getModelsCategoryFilterProvider(modelsCategories))
        break
      default:
        break
    }
    return result
  }, [type, appsProviders, actionsProviders, modelsCategories, modelsPublishers, listingTypeProvider])

  const setQuery = useCallback(
    (filterStr: string) => {
      const {
        type: newType,
        copilotApp: newCopilotApp,
        query: newQuery,
        category: newCategory,
      } = queryFromFilterString(filterStr, hideCopilotExtensions)
      setType(newType)
      setCopilotApp(hideCopilotExtensions ? null : newCopilotApp)
      setCategory(newCategory)
      onQueryChangeV2(newQuery || '', newType)
    },
    [onQueryChangeV2, setType, setCopilotApp, setCategory, hideCopilotExtensions],
  )

  const handleQueryChange = useCallback(
    (value: string) => {
      setPrivateQuery(value)
    },
    [setPrivateQuery],
  )

  const handleSubmit = useCallback(
    (filterQuery: FilterQuery) => {
      setQuery(filterQuery.raw)
    },
    [setQuery],
  )

  const placeholder = useResponsiveValue(
    {
      regular: hideCopilotExtensions
        ? 'Search for apps, actions, and models'
        : 'Search for Copilot extensions, apps, actions, and models',
    },
    'Search in GitHub Marketplace',
  ) as string

  return (
    <Filter
      id="marketplace-search-filter"
      data-testid="marketplace-search-filter"
      providers={providers}
      label="Search Marketplace"
      variant="input"
      filterValue={privateQuery}
      placeholder={placeholder}
      className={`${styles['search-input']}`}
      settings={{
        disableAdvancedTextFilter: true,
        groupAndKeywordSupport: false,
      }}
      onChange={handleQueryChange}
      onSubmit={handleSubmit}
    />
  )
}

try{ MarketplaceSearchField.displayName ||= 'MarketplaceSearchField' } catch {}